/**
 * LayerZero Options utility class for building execution options
 * This is a simplified implementation based on @layerzerolabs/lz-v2-utilities
 */

export class Options {
    private options: string;

    constructor(options = '') {
        this.options = options;
    }

    static newOptions(): Options {
        return new Options('0x0003'); // Options type 3
    }

    /**
     * Add executor LzReceive option
     * @param gas - Gas limit for lzReceive on destination chain
     * @param value - Native token amount to airdrop (in wei)
     */
    addExecutorLzReceiveOption(
        gas: number,
        value: number | bigint = 0,
    ): Options {
        // Option Type 1: LzReceive
        // Format: [option_type(1B)][gas(16B)][value(16B)]
        const optionType = '01';

        // Convert gas to 16 bytes hex (32 characters)
        const gasHex = gas.toString(16).padStart(32, '0');

        // Convert value to 16 bytes hex (32 characters)
        const valueHex = BigInt(value).toString(16).padStart(32, '0');

        // Combine: option_type + gas + value
        const optionData = optionType + gasHex + valueHex;

        // Option length (in bytes) = 1 + 16 + 16 = 33 bytes
        const optionLength = (optionData.length / 2)
            .toString(16)
            .padStart(4, '0');

        this.options += optionLength + optionData;

        return this;
    }

    /**
     * Add executor LzCompose option (for advanced use cases)
     * @param index - Compose index
     * @param gas - Gas limit for lzCompose
     * @param value - Native token amount to airdrop (in wei)
     */
    addExecutorLzComposeOption(
        index: number,
        gas: number,
        value: number | bigint = 0,
    ): Options {
        // Option Type 2: LzCompose
        // Format: [option_type(1B)][index(2B)][gas(16B)][value(16B)]
        const optionType = '02';

        // Convert index to 2 bytes hex (4 characters)
        const indexHex = index.toString(16).padStart(4, '0');

        // Convert gas to 16 bytes hex (32 characters)
        const gasHex = gas.toString(16).padStart(32, '0');

        // Convert value to 16 bytes hex (32 characters)
        const valueHex = BigInt(value).toString(16).padStart(32, '0');

        // Combine: option_type + index + gas + value
        const optionData = optionType + indexHex + gasHex + valueHex;

        // Option length (in bytes) = 1 + 2 + 16 + 16 = 35 bytes
        const optionLength = (optionData.length / 2)
            .toString(16)
            .padStart(4, '0');

        this.options += optionLength + optionData;

        return this;
    }

    /**
     * Convert options to hex string
     */
    toHex(): string {
        return this.options;
    }

    /**
     * Convert options to bytes (for use with ethers.js)
     */
    toBytes(): Uint8Array {
        const hex = this.options.startsWith('0x')
            ? this.options.slice(2)
            : this.options;
        const bytes = new Uint8Array(hex.length / 2);
        for (let i = 0; i < hex.length; i += 2) {
            bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
        }
        return bytes;
    }
}
